import { useRouter } from 'next/router';

import useGetStripeSetupIntent from 'hooks/useGetStripeSetupIntent';
import ErrorIcon from './components/ErrorIcon';
import CheckmarkIcon from './components/CheckmarkIcon';
import WarningIcon from './components/WarningIcon';
import Banner from './components/Banner';
import { SetupIntentStatus, PaymentIntentStatus } from './types';
import useGetStripePaymentIntent from 'hooks/useGetStripePaymentIntent';
import Spinner, { SpinnerType } from 'legacy/common/components/spinner/Spinner';

const SETUP_INTENT_STATUS_MAP = {
  [SetupIntentStatus.Succeeded]: {
    title: 'Payment method added successfully!',
    icon: <CheckmarkIcon />,
    color: 'green'
  },
  [SetupIntentStatus.Processing]: {
    title:
      'We&apos;re processing your payment method. Please check back in a few minutes.',
    icon: <WarningIcon />,
    color: 'yellow'
  },
  [SetupIntentStatus.Canceled]: {
    title: 'Your payment method was not added. Please try again.',
    icon: <WarningIcon />,
    color: 'yellow'
  },
  [SetupIntentStatus.Failed]: {
    title:
      'An error occurred while adding your payment method. Please try again.',
    icon: <ErrorIcon />,
    color: 'red'
  }
};

const PAYMENT_INTENT_STATUS_MAP = {
  [PaymentIntentStatus.Succeeded]: {
    title: 'Payment successful!',
    icon: <CheckmarkIcon />,
    color: 'green'
  },
  [PaymentIntentStatus.Processing]: {
    title:
      'We&apos;re processing your payment. Please check back in a few minutes.',
    icon: <WarningIcon />,
    color: 'yellow'
  },
  [PaymentIntentStatus.Canceled]: {
    title: 'Your payment was not successful. Please try again.',
    icon: <WarningIcon />,
    color: 'yellow'
  },
  [PaymentIntentStatus.RequiresPaymentMethod]: {
    title: 'An error occurred while processing your payment. Please try again.',
    icon: <ErrorIcon />,
    color: 'red'
  }
};

function StripeStatusBanner({ className }: { className?: string }) {
  const { query } = useRouter();

  const { data: paymentIntent, isLoading: isPaymentIntentLoading } =
    useGetStripePaymentIntent(
      query?.payment_intent as string,
      query?.payment_intent_client_secret as string
    );

  const { data: setupIntent, isLoading: isSetupIntentLoading } =
    useGetStripeSetupIntent(
      query?.setup_intent as string,
      query?.setup_intent_client_secret as string
    );

  if (isPaymentIntentLoading || isSetupIntentLoading) {
    return (
      <Banner
        title="Processing payment information..."
        icon={<Spinner type={SpinnerType.PRIMARY} className="mr-6" />}
        color="gray"
        className={className}
      />
    );
  }

  if (!setupIntent && !paymentIntent) {
    return null;
  }

  const bannerData = setupIntent
    ? SETUP_INTENT_STATUS_MAP[setupIntent?.status] ||
      SETUP_INTENT_STATUS_MAP[SetupIntentStatus.Failed]
    : PAYMENT_INTENT_STATUS_MAP[paymentIntent?.status] ||
      PAYMENT_INTENT_STATUS_MAP[PaymentIntentStatus.RequiresPaymentMethod];

  return (
    <Banner
      title={bannerData.title}
      icon={bannerData.icon}
      color={bannerData.color}
      className={className}
    />
  );
}

export default StripeStatusBanner;
